import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Card,
  Alert,
  Spinner,
  Button,
  Row,
  Col,
  Table,
  Badge,
} from "react-bootstrap";
import { AgGridReact } from "ag-grid-react";
import { GridReadyEvent } from "ag-grid-community";
import { useDispatch, useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  getInvoicesTagLevels,
  getTransactionLineMatches,
  createTransactionLineMatch,
  deleteTransactionLineMatch,
} from "src/docuclipper/api";
import { ReduxState } from "src/redux";
import { createAlert } from "src/redux/reducers/Alerts";
import { convertFilterModel, convertSortModel } from "src/utils/filters";
import { datefilterParamsYYYYMMDD } from "../../Analyze/AnalyzeTransactionsTable";
import ExternalFilter from "src/views/Docuclipper/ExternalFilter";
import {
  setExternalFiltersColumnDefs,
  clearExternalFilters,
} from "src/redux/reducers/ExternalFilter";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

interface Props {
  isOpen: boolean;
  onToggle: () => void;
  invoiceTransactionLine: any;
  invoice: any;
  onMatchesUpdated?: () => void;
}

interface POMatch {
  id: number;
  status: string;
  matchedAt: string;
  matchedBy: string;
  DestinationInvoiceMatchIndex?: {
    id: number;
    invoiceId: string;
    documentDate: string;
    vendorNormalized: string;
    customerNormalized: string;
    amount: string;
    documentType: string;
  };
}

const getType = (documentType) => {
  if (["payables", "bill", "expense"].includes(documentType)) {
    return "payables";
  } else if (["receivables", "invoice"].includes(documentType)) {
    return "receivables";
  }
  throw new Error(`Unknown document type: ${documentType}`);
};

const formatData = (data, currentInvoiceId) => {
  if (!Array.isArray(data)) {
    console.warn("formatData received non-array data:", data);
    return [];
  }

  return data
    .filter((item) => item.transactionLineId !== currentInvoiceId)
    .map((item) => ({
      id: item.transactionLineId,
      invoiceId: item.invoiceId,
      invoiceDate: item.documentDate,
      invoiceVendor: item.vendorNormalized || "",
      invoiceCustomer: item.customerNormalized || "",
      invoiceAmount: parseFloat(item.amount) || 0,
      invoiceType: getType(item.documentType),
      docType: item.documentType,
      approvalStatus: item.approvalStatus || "",
      hasPOMatch: String(Boolean(item.hasPOMatch)),
      hasBankMatch: String(Boolean(item.hasBankMatch)),
    }));
};

export const InvoicePOMatchManager: React.FC<Props> = ({
  isOpen,
  onToggle,
  invoiceTransactionLine,
  invoice,
  onMatchesUpdated,
}) => {
  const dispatch = useDispatch();
  const gridApi = useRef<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [selectedPOs, setSelectedPOs] = useState<any[]>([]);
  const [existingMatches, setExistingMatches] = useState<POMatch[]>([]);
  const [isLoadingMatches, setIsLoadingMatches] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
  });

  const { externalFilters } = useSelector(
    (state: ReduxState) => state.ExternalFilter
  );
  const job = useSelector((state: ReduxState) => state.JobData.job);
  const { selectedL1: l1, selectedL2: l2 } = useSelector(
    (state: ReduxState) => state.TagLevel
  );

  const externalFilterColumns = useMemo(
    () => [
      {
        field: "invoiceId",
        label: "PO ID",
        filter: true,
        filterType: "text",
        defaultFilterType: "contains",
        configurable: true,
      },
      {
        field: "invoiceDate",
        label: "Date",
        filter: true,
        filterType: "date",
        defaultFilterType: "equals",
        configurable: true,
      },
      {
        field: "invoiceAmount",
        label: "Amount",
        filter: true,
        filterType: "number",
        defaultFilterType: "equals",
        configurable: true,
      },
      {
        field: "invoiceVendor",
        label: "Vendor",
        filter: true,
        filterType: "text",
        defaultFilterType: "contains",
        configurable: true,
      },
      {
        field: "invoiceCustomer",
        label: "Customer",
        filter: true,
        filterType: "text",
        defaultFilterType: "contains",
        configurable: true,
      },
    ],
    []
  );

  // Load existing PO matches when component opens
  useEffect(() => {
    if (isOpen && invoiceTransactionLine?.id) {
      loadExistingMatches();
    }
  }, [isOpen, invoiceTransactionLine?.id]);

  const loadExistingMatches = async () => {
    setIsLoadingMatches(true);
    try {
      const matches = await getTransactionLineMatches(
        "invoice2po",
        Number(invoiceTransactionLine.id)
      );
      setExistingMatches(matches.matches || []);
    } catch (error) {
      console.error("Error loading existing PO matches:", error);
      dispatch(
        createAlert({
          message: "Error loading existing PO matches",
          type: "error",
          timeout: 5000,
        })
      );
    } finally {
      setIsLoadingMatches(false);
    }
  };

  const getRows = useCallback(
    (params) => {
      if (!l1 || !l2) {
        params.failCallback();
        return;
      }

      try {
        setLoading(true);
        getInvoicesTagLevels(l1, l2, {
          pagination: {
            page: Math.floor(params.startRow / pagination.pageSize) + 1,
            pageSize: params.endRow - params.startRow,
          },
          filters: [
            ...convertFilterModel(params),
            // Filter out the current invoice - we'll handle this in the formatting
          ],
          sorting: convertSortModel(params.sortModel),
        })
          .then((response) => {
            setPagination(response.pagination);
            params.successCallback(
              formatData(response.data, invoiceTransactionLine?.id),
              response.pagination.totalCount
            );
          })
          .catch((err) => {
            params.failCallback();
            dispatch(
              createAlert({
                message: err.message || "Error fetching potential PO matches",
                timeout: 0,
                type: "error",
              })
            );
          })
          .finally(() => {
            setLoading(false);
          });
      } catch (err) {
        params.failCallback();
        dispatch(
          createAlert({
            message:
              err instanceof Error
                ? err.message
                : "Error fetching potential PO matches",
            timeout: 0,
            type: "error",
          })
        );
        setLoading(false);
      }
    },
    [
      job,
      pagination.pageSize,
      externalFilters,
      l1,
      l2,
      invoiceTransactionLine?.id,
    ]
  );

  const dataSource = useMemo(
    () => ({
      rowCount: undefined,
      getRows,
    }),
    [getRows]
  );

  useEffect(() => {
    if (gridApi.current) {
      gridApi.current.setDatasource(dataSource);
    }
  }, [dataSource, getRows]);

  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      gridApi.current = params.api;
      gridApi.current.setDatasource(dataSource);

      // Initialize external filters after grid is ready
      dispatch(clearExternalFilters());
      dispatch(
        setExternalFiltersColumnDefs({
          externalFilterColumnDefs: externalFilterColumns,
        })
      );
    },
    [dataSource, dispatch, externalFilterColumns]
  );

  const SelectionRenderer = (props: any) => {
    if (!props.data || !props.data.id) {
      return <></>;
    }

    const isSelected = selectedPOs.some((po) => po.id === props.data.id);

    const handleToggleSelect = (e: any) => {
      e.stopPropagation();
      if (isSelected) {
        setSelectedPOs((prev) => prev.filter((po) => po.id !== props.data.id));
      } else {
        setSelectedPOs((prev) => [...prev, props.data]);
      }
    };

    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100%" }}
      >
        <input
          type="checkbox"
          checked={isSelected}
          onChange={handleToggleSelect}
          style={{ transform: "scale(1.2)" }}
        />
      </div>
    );
  };

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Select",
        cellRenderer: SelectionRenderer,
        sortable: false,
        filter: false,
        width: 60,
        pinned: "left" as const,
      },
      {
        field: "invoiceId",
        headerName: "PO ID",
        sortable: true,
        filter: true,
        width: 120,
      },
      {
        field: "invoiceVendor",
        headerName: "Vendor",
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        field: "invoiceCustomer",
        headerName: "Customer",
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        field: "invoiceAmount",
        headerName: "Amount",
        sortable: true,
        filter: "agNumberColumnFilter",
        width: 120,
        valueFormatter: (params: any) =>
          params.value
            ? new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              }).format(params.value)
            : "",
      },
      {
        field: "invoiceDate",
        headerName: "Date",
        sortable: true,
        filter: "agDateColumnFilter",
        filterParams: datefilterParamsYYYYMMDD,
        width: 120,
      },
    ],
    [selectedPOs]
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      filter: true,
      sortable: true,
    }),
    []
  );

  // Delete existing match
  const handleDeleteExistingMatch = async (matchId: number) => {
    try {
      await deleteTransactionLineMatch(matchId);

      // Remove from local state
      setExistingMatches((prev) =>
        prev.filter((match) => match.id !== matchId)
      );

      // Notify parent component
      if (onMatchesUpdated) {
        onMatchesUpdated();
      }

      dispatch(
        createAlert({
          message: "PO match deleted successfully",
          type: "success",
          timeout: 3000,
        })
      );
    } catch (error) {
      console.error("Error deleting PO match:", error);
      dispatch(
        createAlert({
          message: "Error deleting PO match",
          type: "error",
          timeout: 5000,
        })
      );
    }
  };

  // Remove PO from selected list
  const handleRemovePO = (poId: any) => {
    setSelectedPOs((prev) => prev.filter((po) => po.id !== poId));
  };

  // Create matches using real API
  const handleCreateMatch = async () => {
    if (!invoiceTransactionLine || selectedPOs.length === 0) {
      dispatch(
        createAlert({
          message: "Please select at least one invoice to match",
          type: "error",
          timeout: 5000,
        })
      );
      return;
    }

    setLoading(true);

    try {
      // Prepare matches array
      const matchesToCreate = selectedPOs.map((po) => ({
        sourceTransactionLineId: Number(invoiceTransactionLine.id),
        destinationTransactionLineId: Number(po.id),
      }));

      console.log("Creating PO matches:", matchesToCreate);

      // Create all matches at once
      const response = await createTransactionLineMatch(
        matchesToCreate,
        "invoice2po",
        {
          matchedAt: new Date().toISOString(),
          matchedBy: "user",
        }
      );

      console.log("API response:", response);

      // Reload existing matches to get complete data
      await loadExistingMatches();

      // Clear selected POs
      setSelectedPOs([]);

      // Notify parent component
      if (onMatchesUpdated) {
        onMatchesUpdated();
      }

      dispatch(
        createAlert({
          message: `Successfully matched invoice to ${selectedPOs.length} PO(s)`,
          type: "success",
          timeout: 5000,
        })
      );
    } catch (error) {
      console.error("Error creating PO matches:", error);
      dispatch(
        createAlert({
          message: `Error creating PO matches: ${
            error instanceof Error ? error.message : String(error)
          }`,
          type: "error",
          timeout: 5000,
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setSelectedPOs([]);
    onToggle();
  };

  const totalMatches = existingMatches.length + selectedPOs.length;

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 1049,
        }}
        onClick={onToggle}
      />

      {/* Drawer */}
      <div
        className={`invoice-po-matcher-drawer ${isOpen ? "open" : "closed"}`}
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          height: "100vh",
          width: isOpen ? "70%" : "0",
          backgroundColor: "#fff",
          boxShadow: "-2px 0 10px rgba(0,0,0,0.1)",
          transition: "width 0.3s ease-in-out",
          zIndex: 1050,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drawer Header */}
        <div
          style={{
            padding: "1rem 1.5rem",
            borderBottom: "1px solid #dee2e6",
            backgroundColor: "#f8f9fa",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h5 className="mb-0">
            Match Invoice to Purchase Orders
            {totalMatches > 0 && (
              <Badge variant="primary" className="ml-2">
                {totalMatches} match{totalMatches !== 1 ? "es" : ""}
              </Badge>
            )}
          </h5>
          <div>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={onToggle}
              className="mr-2"
            >
              <FontAwesomeIcon
                icon={isOpen ? "chevron-right" : "chevron-left"}
              />
            </Button>
          </div>
        </div>

        {/* Drawer Content */}
        <div style={{ flex: 1, overflow: "auto", padding: "1.5rem" }}>
          {/* Invoice Summary Banner */}
          {invoice && invoiceTransactionLine && (
            <Alert variant="info" className="mb-3">
              <div className="d-flex justify-content-between">
                <div>
                  <strong>Invoice #{invoice.id || "N/A"}</strong>
                  <div className="mt-1">
                    <small>
                      <strong>Date:</strong> {invoice.date || "N/A"} |{" "}
                      <strong>Amount:</strong> $
                      {parseFloat(invoice.total || "0").toFixed(2)} |{" "}
                      <strong>
                        {["receivables", "invoice"].includes(invoice.docType) &&
                          "Customer"}
                        {["payables", "bill", "expense"].includes(
                          invoice.docType
                        ) && "Vendor"}
                        :
                      </strong>{" "}
                      {invoice.docType === "receivables" &&
                        (invoice.customerExcelEdited ||
                          invoice.customerExcelAutomated ||
                          invoice.customerExcel ||
                          "N/A")}
                      {invoice.docType === "payables" &&
                        (invoice.vendorExcelEdited ||
                          invoice.vendorExcelAutomated ||
                          invoice.vendorExcel ||
                          "N/A")}
                      {["bill", "expense"].includes(invoice.docType) &&
                        (invoice.vendorEditedName ||
                          invoice.vendorAutomatedName ||
                          invoice.vendorName ||
                          "N/A")}
                      {["invoice"].includes(invoice.docType) &&
                        (invoice.customerEditedName ||
                          invoice.customerAutomatedName ||
                          invoice.customerName ||
                          "N/A")}
                      {" | "}
                      <strong>PO Number:</strong> {invoice.poNumber || "N/A"}
                    </small>
                  </div>
                </div>
              </div>
            </Alert>
          )}

          {/* Existing Matches Section */}
          {existingMatches.length > 0 && (
            <div className="mb-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h5 className="mb-0 text-primary">
                  <FontAwesomeIcon icon="link" className="mr-2" />
                  Existing PO Matches ({existingMatches.length})
                </h5>
                {isLoadingMatches && (
                  <FontAwesomeIcon icon="spinner" spin className="text-muted" />
                )}
              </div>
              <div
                style={{
                  maxHeight: "200px",
                  overflowY: "auto",
                  border: "1px solid #dee2e6",
                  borderRadius: "0.375rem",
                }}
              >
                <Table striped bordered hover size="sm" className="mb-0">
                  <thead style={{ backgroundColor: "#f8f9fa" }}>
                    <tr>
                      <th>PO ID</th>
                      <th>Date</th>
                      <th>Vendor</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {existingMatches.map((match) => (
                      <tr key={match.id}>
                        <td>
                          {match.DestinationInvoiceMatchIndex?.invoiceId ||
                            "N/A"}
                        </td>
                        <td>
                          {match.DestinationInvoiceMatchIndex?.documentDate
                            ? new Date(
                                match.DestinationInvoiceMatchIndex.documentDate
                              ).toLocaleDateString()
                            : "N/A"}
                        </td>
                        <td>
                          {match.DestinationInvoiceMatchIndex
                            ?.vendorNormalized ||
                            match.DestinationInvoiceMatchIndex
                              ?.customerNormalized ||
                            "N/A"}
                        </td>
                        <td>
                          $
                          {parseFloat(
                            match.DestinationInvoiceMatchIndex?.amount || "0"
                          ).toFixed(2)}
                        </td>
                        <td>
                          <Badge
                            variant={
                              match.status === "approved"
                                ? "success"
                                : match.status === "rejected"
                                ? "danger"
                                : "warning"
                            }
                          >
                            {match.status}
                          </Badge>
                        </td>
                        <td>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteExistingMatch(match.id)}
                          >
                            <FontAwesomeIcon icon="trash" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          )}

          {/* Selected POs Section */}
          {selectedPOs.length > 0 && (
            <div className="mb-4">
              <h5 className="mb-3 text-primary">
                <FontAwesomeIcon icon="plus" className="mr-2" />
                Selected Purchase Orders ({selectedPOs.length})
              </h5>
              <div
                style={{
                  border: "1px solid #dee2e6",
                  borderRadius: "0.375rem",
                }}
              >
                <Table striped bordered hover size="sm" className="mb-0">
                  <thead style={{ backgroundColor: "#f8f9fa" }}>
                    <tr>
                      <th>Invoice ID</th>
                      <th>Date</th>
                      <th>Vendor</th>
                      <th>Amount</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedPOs.map((po) => (
                      <tr key={po.id}>
                        <td>{po.invoiceId || "N/A"}</td>
                        <td>{po.invoiceDate || "N/A"}</td>
                        <td>{po.invoiceVendor || "N/A"}</td>
                        <td>${parseFloat(po.invoiceAmount || 0).toFixed(2)}</td>
                        <td>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleRemovePO(po.id)}
                          >
                            <FontAwesomeIcon icon="trash" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Action buttons for new matches */}
              <div className="mt-3 d-flex gap-2">
                <Button
                  variant="primary"
                  onClick={handleCreateMatch}
                  disabled={loading}
                  className="mr-1"
                >
                  {loading && (
                    <FontAwesomeIcon icon="spinner" spin className="mr-1" />
                  )}
                  Create PO Match{selectedPOs.length > 1 ? "es" : ""}
                </Button>
                <Button variant="secondary" onClick={handleCancel}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Search and Filter Section */}
          <div className="mb-3">
            <h5 className="mb-3 text-primary">
              <FontAwesomeIcon icon="search" className="mr-2" />
              Find Purchase Orders to Match
            </h5>

            <Row className="mb-3">
              <Col md="12">
                <Row className="px-3">
                  <span className="ml-2 mr-2">Filter by</span>
                  {gridApi.current && (
                    <ExternalFilter
                      columnDefs={externalFilterColumns}
                      gridApi={gridApi.current}
                    />
                  )}
                </Row>
              </Col>
            </Row>

            <div
              className="ag-theme-alpine"
              style={{ height: "400px", width: "100%" }}
            >
              <AgGridReact
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                onGridReady={onGridReady}
                rowModelType="infinite"
                cacheBlockSize={pagination.pageSize}
                cacheOverflowSize={2}
                maxConcurrentDatasourceRequests={1}
                infiniteInitialRowCount={1}
                maxBlocksInCache={10}
                suppressCellFocus={true}
                rowSelection="multiple"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
